import * as React from 'react'
import type { AidaOverlayOrigin } from '../../stores/overlay-store'
import {
  AIDA_OVERLAY_OPEN_DURATION_MS,
  fullscreenRestingFrame,
  overlayFrameStyle,
  prefersReducedMotion,
} from '../../utils/overlay/overlay-animation'

export function useOpenShell(origin: AidaOverlayOrigin | null, enabled: boolean) {
  const [grown, setGrown] = React.useState(false)
  const playedOriginRef = React.useRef<AidaOverlayOrigin | null>(null)
  const animationRef = React.useRef<Animation | null>(null)

  React.useEffect(() => {
    if (!origin) {
      animationRef.current?.cancel()
      animationRef.current = null
      playedOriginRef.current = null
      setGrown(false)
      return
    }
    if (!enabled) {
      animationRef.current?.cancel()
      animationRef.current = null
      if (playedOriginRef.current === origin) {
        setGrown(true)
      }
    }
  }, [enabled, origin])

  const shellRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      if (!node || !origin || !enabled || playedOriginRef.current === origin) {
        return
      }
      playedOriginRef.current = origin

      if (typeof node.animate !== 'function') {
        setGrown(true)
        return
      }

      const animation = node.animate(
        [overlayFrameStyle(origin), overlayFrameStyle(fullscreenRestingFrame())],
        {
          duration: AIDA_OVERLAY_OPEN_DURATION_MS,
          easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
          fill: 'both',
        },
      )
      animationRef.current = animation
      animation.addEventListener('finish', () => {
        if (animationRef.current !== animation) {
          return
        }
        animationRef.current = null
        setGrown(true)
      })
    },
    [enabled, origin],
  )

  const showShell = enabled && origin !== null && !grown && !prefersReducedMotion()
  return { shellRef, showShell }
}
